import { BaseErrorModel, PaginatedSelectModel, SelectModel } from "@/models/core.model";
import { AxiosError } from "axios";
import React, { useState, useEffect, useCallback } from "react";
import { SelectPicker, Button, SelectPickerProps, Loader } from "rsuite";
import ErrorMessage from "./ErrorMessage";
import { debounce } from "lodash";

type FetchingSelectProps = {
  fetchData: (keyword: string, page: number) => Promise<PaginatedSelectModel>
  placeholder?: string;
  onChange?: (value: any) => void;
  value?: any;
  reFetchOnOpen?: boolean
} & Omit<SelectPickerProps<any>, "data" | "onSearch">;

function FetchingSelect({
  fetchData,
  placeholder = "Select...",
  onChange,
  value,
  reFetchOnOpen = false,
  ...rest
}: FetchingSelectProps) {
  const [data, setData] = useState<SelectModel[]>([]);
  const [searchData, setSearchData] = useState<SelectModel[]>([]);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [isLastPage, setIsLastPage] = useState(false);

  const loadData = useCallback(
    async (reset = false) => {
      if (loading) return;
      setLoading(true);
      try {
        const nextPage = reset ? 1 : page;
        const result = await fetchData(keyword, nextPage);

        if (reset) {
          setData(result.data);
        } else {
          addData(result.data);
        }

        setIsLastPage(result.isLastPage);
        setPage(nextPage + 1);

        } catch (error) {
            setErrorModel((error as AxiosError<BaseErrorModel>).response?.data);
        } finally {
        setLoading(false);
      }
    },
    [fetchData, page, loading]
  );

  const searchKeyword = useCallback(
    debounce(async (keyword: string) => {
      if (loading) return;

      if(!keyword){
        setKeyword(keyword);
        return;
      }

      setLoading(true);
      try {
        const result = await fetchData(keyword, 1);
        setSearchData(result.data);
        addData(result.data);
        setKeyword(keyword);
      } finally {
        setLoading(false);
      }
    }, 600),
    [keyword]
  );
  
  const addData = (newItems: SelectModel[]) => {
    setData((prev) => {
      const existingIds = new Set(prev.map((item) => item.value));
      const filtered = newItems.filter(
        (item) => !existingIds.has(item.value)
      );
      return [...prev, ...filtered];
    });
  };

  // Ensure the current value exists in data (load more pages until found)
  useEffect(() => {
    const ensureValueExists = async () => {
      // console.log(rest.label, "value changed.", value);
      if (!value) return;

      // Check if already exists in current data
      const exists = data.some((item) => item.value === value);
      if (exists) return;

      // Otherwise, load more pages until found or no more data
      let currentPage = page;
      let found = false;
      let lastPage = false;

      while (!lastPage && !found) {
        const result = await fetchData(keyword, currentPage);
        addData(result.data);

        found = result.data.some((item) => item.value === value);
        lastPage = result.isLastPage;
        setIsLastPage(lastPage);
        setPage(currentPage + 1);

        currentPage++;
      }
    };

    ensureValueExists();
  }, [value]);
  
// Function to render menu and handle loading and error
  const renderMenu = (menu: any) => {
    if (loading) {
      return (
        <>
          {menu}
          <div style={{ padding: 4, color: '#999', textAlign: 'center' }}>
            <Loader size="sm" /> Loading...
          </div>
        </>
      );
    }
    if (errorModel) {
      return (
        <div style={{ margin: 10 }}>
          <ErrorMessage model={errorModel}></ErrorMessage>
        </div>
      );
    }
    return (
        <>
          {menu}
          {!isLastPage && !keyword && <div style={{ padding: 4}}>
            <Button block onClick={()=> loadData(false)}>Load More</Button>
          </div>}
        </>
      );
  };

  return (
    <SelectPicker 
      value={value ?? null}
      data={keyword ? searchData : data}
      onChange={onChange}
      placeholder={placeholder}
      onSearch={val => searchKeyword(val)}
      onOpen={() => {if(reFetchOnOpen || data.length == 0) loadData(true);}}
      renderListbox={renderMenu}
      {...rest}
    />
  );
}

export default FetchingSelect;